import {
  IQE_PROJECT_STATUS_MESSAGE_MAP,
  DEFAULT_EMPTY_STATE_MESSAGE,
  VALID_APP_URLS,
} from '../constants';

import type { IqeProjectStatus, TestRun } from '../interfaces/ui';

import type { ProjectMappingType } from '../interfaces/ui/addProjectModal';

/**
 * Validates if a given URL is in the allowed list of non-prod app URLs.
 */
export const isValidAppUrl = (url: string): boolean =>
  VALID_APP_URLS.some(valid => valid.toLowerCase() === url.toLowerCase());

/**
 * Returns the CSS class name suffix for a given environment string.
 */
export const getEnvironmentClass = (env: string): string => {
  switch (env.toLowerCase()) {
    case 'dev':
      return 'env-dev';
    case 'sit':
      return 'env-sit';
    case 'qa':
      return 'env-qa';
    case 'uat':
      return 'env-uat';
    case 'non-prod':
      return 'env-nonprod';
    default:
      return 'env-dev';
  }
};

export function toSystemValue(
  projectMappingType: ProjectMappingType,
): 'ado' | 'qmetry' {
  return projectMappingType === 'ADO' ? 'ado' : 'qmetry';
}

export const getProjectStatusMessage = (status: IqeProjectStatus): string => {
  if (!status) return DEFAULT_EMPTY_STATE_MESSAGE;
  return IQE_PROJECT_STATUS_MESSAGE_MAP[status] ?? DEFAULT_EMPTY_STATE_MESSAGE;
};

/**
 * Safely parses a date string and handles null/undefined values.
 * Returns null if the date string is invalid or missing.
 */
export const parseDateSafely = (dateString?: string): Date | null => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

/**
 * Sorts TestRun array by createdAt date in descending order.
 * Handles null dates by placing them at the end.
 */
export const sortRunsByDateDescending = (runs: TestRun[]): TestRun[] => {
  return [...runs].sort((a, b) => {
    if (a.createdAt && b.createdAt) {
      return b.createdAt.getTime() - a.createdAt.getTime();
    }
    if (a.createdAt && !b.createdAt) return -1;
    if (!a.createdAt && b.createdAt) return 1;
    return 0;
  });
};
